import { t } from '../i18n/lingua'
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'

import { useAuth } from '../auth/AuthContext'
import { livelloRotta, primaRottaAperta, usePermessiModuli } from '../auth/permessi'
import { BUILD, buildLabel, isStale } from '../build'
import LinguaSwitch from './LinguaSwitch'
import Marchio from './Marchio'
import { useStatoEagle } from '../hooks/useEagle'
import { useStatoMed } from '../hooks/useMed'
import { useStatoAccess } from '../hooks/useAccess'
import { DOPO_CAMBIO_TENANT } from '../auth/sezioneTenant'
import { useAsync } from '../hooks/useAsync'
import { api } from '../api/client'
import DobermannBadge from './DobermannBadge'
import Assistente from './Assistente'
import GlobalSearch from './GlobalSearch'
import HoneypotBadge from './HoneypotBadge'
import { costruisciAree, IconaArea, PortaleContext, SelettoreAree, type AreaId, type AreaVisibile } from './Aree'
import { NavRail, StarButton } from './NavRail'
import NotificheBell from './NotificheBell'
import ThemeSwitch from './ThemeSwitch'

/**
 * Navigazione raggruppata per area, come nei servizi AWS.
 *
 * I gruppi non sono decorazione: con ventiquattro voci in fila l'occhio non
 * trova niente, mentre "Inventario / Raccolta / Amministrazione" dice a colpo
 * d'occhio dove cercare.
 *
 * `id` è l'identità **stabile** del gruppo: la usano la memoria delle sezioni
 * chiuse e la scelta dell'icona. Non si usa l'etichetta perché è tradotta —
 * cambiando lingua cambierebbe la chiave, e le sezioni chiuse in italiano
 * risulterebbero aperte in inglese.
 *
 * Una funzione e non una costante: le etichette passano da `t()`, che va
 * chiamata quando si disegna, non all'import del modulo.
 */
type GruppoNav = {
  id: string
  /** The portal area (Discovery, Planning…); `home` is the home page, always shown. */
  area: AreaId | 'home'
  section: string
  /** `anche`: the page lives in another area first; here it is a second door
   *  (a link to it opens its first area, unless the area in use has it). */
  items: { to: string; label: string; end?: boolean; anche?: boolean }[]
  /**
   * A quale prodotto appartiene la sezione (ADR-0022): `censo` (predefinito),
   * `eagle`, oppure `comune` — la raccolta e l'amministrazione servono a
   * entrambi. Con CENSO ed Eagle insieme il menu ha due voci principali, ognuna
   * con le sue sezioni; con uno solo il menu è piatto come sempre.
   */
  prodotto?: 'censo' | 'eagle' | 'comune' | 'med' | 'access'
}

function costruisciNav(): GruppoNav[] {
  return [
  {
    // La home del portale: le aree e la dashboard. Fuori dalle aree, sempre in
    // cima al menu.
    id: 'panoramica',
    area: 'home',
    section: t('Home'),
    items: [{ to: '/', label: t('Home'), end: true }],
  },

  // ---------------------------------------------------------- Discovery
  {
    // La raccolta per prima: è il «come» CENSO arriva a sapere ciò che c'è. I
    // pool prima dei piani e dei job: sono il «dove», e si prepara prima di
    // decidere il «come» e prima di lanciare.
    id: 'raccolta',
    area: 'discovery',
    prodotto: 'comune',
    section: t('Raccolta'),
    items: [
      { to: '/pools', label: t('Pool di asset') },
      { to: '/discovery-plans', label: t('Piani di discovery') },
      { to: '/jobs', label: t('Job scansioni') },
      { to: '/schedules', label: t('Scheduler') },
      { to: '/fleet', label: t('Sonde') },
      // I gruppi hanno una voce propria e non una scheda dentro «Sonde»: si
      // creano guardando la topologia dei siti, non la singola sonda.
      { to: '/probe-groups', label: t('Gruppi di sonde') },
      { to: '/credentials', label: t('Credenziali') },
      // «Cosa è cambiato stanotte», non «com'è fatto questo switch».
      { to: '/device-configs', label: t('Configurazioni apparati') },
      // Un modo di raccogliere, non un tipo di sonda.
      { to: '/inventory-agents', label: t('Agenti d\u2019inventario') },
    ],
  },
  {
    id: 'inventario',
    area: 'discovery',
    section: t('Inventario'),
    items: [
      { to: '/cis', label: t('Configuration Item') },
      // La rete prima della topologia: «cosa c'è attaccato alla porta 24, e
      // perché è giù» è la domanda che si fa più spesso, e sulla mappa non ha
      // risposta.
      { to: '/network', label: t('Rete') },
      { to: '/firmware', label: t('Firmware (TFTP)') },
      { to: '/topology', label: t('Topologia') },
      { to: '/virtual-map', label: t('Ambienti virtuali') },
      { to: '/vinventory', label: t('Inventario virtuale') },
      { to: '/cloud-map', label: t('Ambienti cloud') },
      { to: '/cinventory', label: t('Inventario cloud') },
      { to: '/container-inventory', label: t('Inventario container') },
      { to: '/addresses', label: t('Inventario indirizzi') },
      // I tag: catalogo e regole. Si mettono sugli oggetti dalle loro pagine.
      { to: '/tags', label: t('Tag') },
    ],
  },
  {
    // I servizi: è il livello a cui si ragiona quando qualcosa non va («il
    // portale è lento»); le Business App sono ciò di cui sono fatti.
    id: 'servizi',
    area: 'discovery',
    section: t('Servizi'),
    items: [
      { to: '/services', label: t('Business service') },
      { to: '/business-apps', label: t('Business App') },
    ],
  },
  {
    // Il software installato (CENSO SBOM) e quello che l'organizzazione produce.
    id: 'software',
    area: 'discovery',
    section: t('Inventario Software'),
    items: [
      { to: '/software-flow', label: t('Mappa Software') },
      { to: '/software-map', label: t('Application Maintenance') },
      { to: '/software-list', label: t('Elenco dei software') },
    ],
  },
  {
    // Tre domande sugli stessi dati — chi parla con chi, cosa è esposto, se la
    // raccolta funziona — nell'ordine in cui si cercano.
    id: 'traffico',
    area: 'discovery',
    section: t('Traffico'),
    items: [
      { to: '/flows', label: t('Conversazioni'), end: true },
      { to: '/flows/entry-points', label: t('Entry point') },
      // La mappa dei flussi aggregati: chi parla con chi, nel tempo.
      { to: '/flows/map', label: t('Mappa correlazione') },
      { to: '/flows/exporters', label: t('Esportatori') },
      // La lettura «da analista» del traffico: protocolli, riconoscimento,
      // inventari di riferimento.
      { to: '/flows/analysis', label: t('Traffic Analysis') },
      { to: '/routing', label: t('Routing') },
    ],
  },
  {
    // La raccolta porta i dati **dentro** CENSO; i connettori li portano fuori
    // (o fanno entrare ciò che CENSO non può scoprire).
    id: 'integrazioni',
    area: 'discovery',
    section: t('Integrazioni'),
    items: [
      { to: '/integrations', label: t('Sorgenti dati') },
      { to: '/dns', label: 'DNS' },
      { to: '/backup', label: 'Backup' },
      { to: '/ipam', label: 'IPAM' },
      { to: '/awx', label: t('Ansible AWX') },
      { to: '/connectors', label: t('Connettori') },
    ],
  },

  // ----------------------------------------------------------- Planning
  {
    // «Quanto margine resta e quando finisce»: una domanda di pianificazione,
    // diversa da «cosa c'è».
    id: 'capacity',
    area: 'planning',
    section: t('Capacity'),
    items: [{ to: '/capacity', label: t('Capacity plan') }],
  },
  {
    id: 'ciclo_vita',
    area: 'planning',
    section: t('Cicli di vita e costi'),
    items: [
      { to: '/software-eol', label: t('Scadenze dei software') },
      // I costi degli account cloud: un costo cambia ogni giorno e ha una
      // storia, la configurazione no.
      { to: '/cost-center', label: t('Costi Cloud') },
      // Anche qui, oltre che in Security: qui si pianificano le finestre, là si
      // guarda il rischio.
      { to: '/patching', label: t('Patching'), anche: true },
    ],
  },
  {
    // Attraversano inventario, beni, contratti e discovery.
    id: 'analisi',
    area: 'planning',
    section: t('Analisi'),
    items: [
      { to: '/reports', label: t('Report') },
      // Cosa compare e cosa non si trova più nel tempo.
      { to: '/scan-stats', label: t('Statistiche scansioni') },
    ],
  },

  // --------------------------------------------------------- Monitoring
  {
    // Eagle, il monitoraggio (ADR-0022): add-on nascosto. L'area compare solo
    // se il tenant ce l'ha (`useEagle`), altrimenti non esiste.
    id: 'eagle',
    area: 'monitoring',
    section: 'Eagle',
    items: [
      { to: '/eagle', label: t('Cruscotto'), end: true },
      { to: '/eagle/cis', label: t('CI monitorati') },
      { to: '/eagle/sonde', label: t('Sonde') },
      { to: '/eagle/notifiche', label: t('Notifiche') },
      { to: '/eagle/report', label: t('Report') },
      { to: '/eagle/profili', label: t('Profili') },
      { to: '/eagle/parete', label: t('Sinottici pubblicati') },
      { to: '/eagle/cruscotti', label: t('Gestione cruscotti') },
    ],
    prodotto: 'eagle',
  },

  // ----------------------------------------------------------- Security
  {
    // La postura: «cosa mi preoccupa», una domanda diversa da «cosa c'è».
    id: 'sicurezza',
    area: 'security',
    section: t('Sicurezza'),
    items: [
      { to: '/security-health', label: t('Security health') },
      { to: '/ai-usage', label: t('Utilizzo AI') },
      { to: '/encryption', label: t('Cifratura'), end: true },
      // The register of the certificates the machines expose (a tab of
      // Cifratura): its own entry, so it is found from the menu and from the
      // page search instead of only from inside Cifratura.
      { to: '/encryption/certificates', label: t('Anagrafica certificati') },
      { to: '/certificates', label: t('Gestione certificati') },
      { to: '/bitlocker', label: t('BitLocker') },
      { to: '/patching', label: t('Patching') },
    ],
  },
  {
    // Le difese attive (chi parla senza essere censito, chi bussa a una porta
    // che non dovrebbe esistere) e le sorgenti di sicurezza interrogate in
    // tempo reale.
    id: 'difese',
    area: 'security',
    section: t('Difese e sorgenti'),
    items: [
      { to: '/dobermann', label: 'Dobermann' },
      { to: '/honeypot', label: 'Honeypot' },
      { to: '/wazuh', label: 'Wazuh' },
      { to: '/cynet', label: 'Cynet' },
      { to: '/firewall', label: t('Firewall') },
    ],
  },

  // ---------------------------------------------------------- DevSecOps
  {
    // Il software che l'organizzazione costruisce e pubblica: le schede di
    // «Software prodotto», ognuna con il suo indirizzo (si mette fra i
    // preferiti, si apre da ⌘K).
    id: 'devsecops_software',
    area: 'devsecops',
    section: t('Software prodotto'),
    items: [
      { to: '/software-supply', label: t('Panoramica'), end: true },
      { to: '/software-supply/projects', label: t('Progetti') },
      { to: '/software-supply/pipelines', label: t('Pipeline') },
      { to: '/software-supply/registries', label: t('Registri') },
      { to: '/software-supply/components', label: t('Componenti') },
      { to: '/software-supply/vulnerabilities', label: t('Vulnerabilit\u00e0') },
    ],
  },
  {
    // Ciò che CENSO fa e decide nelle pipeline, e dove si collega.
    id: 'devsecops_governo',
    area: 'devsecops',
    section: t('Sicurezza della pipeline'),
    items: [
      { to: '/software-supply/gate', label: t('Cancello di sicurezza') },
      { to: '/software-supply/vex', label: t('Valutazioni VEX') },
      { to: '/software-supply/changes', label: t('Modifiche in GitLab') },
      { to: '/software-supply/connections', label: t('Connessioni') },
      // Il registro CRA nasce dai rilasci e dagli SBOM di qui: anche qui,
      // oltre che in Compliance.
      { to: '/cra', label: t('CRA Compliance'), anche: true },
    ],
  },

  // -------------------------------------------------------------- Asset
  {
    // Il registro dei beni si popola **dai** configuration item.
    id: 'beni',
    area: 'asset',
    section: t('Beni'),
    items: [
      { to: '/assets', label: t('Asset') },
      { to: '/software-licenses', label: t('Licenze dei software') },
      // Centri di costo, sedi, unità: anche in Settings.
      { to: '/master-data', label: t('Anagrafiche') },
    ],
  },

  // ---------------------------------------------------------- Contracts
  {
    // The registry of the third parties: proposed from what CENSO discovers,
    // confirmed by a person (supplier_service).
    id: 'fornitori',
    area: 'contracts',
    section: t('Fornitori'),
    items: [
      { to: '/suppliers', label: t('Panoramica fornitori'), end: true },
      { to: '/suppliers/list', label: t('Fornitori') },
      { to: '/suppliers/catalog', label: t('Da catalogare') },
      { to: '/suppliers/requests', label: t('Richieste ai fornitori') },
    ],
  },
  {
    id: 'contratti',
    area: 'contracts',
    section: t('Contratti'),
    items: [
      { to: '/contracts', label: t('Contratti') },
      { to: '/software-licenses', label: t('Licenze dei software'), anche: true },
    ],
  },

  // --------------------------------------------------------- Compliance
  {
    // La conformità si legge dopo aver visto lo stato: è la stessa fotografia,
    // inquadrata dalla norma.
    id: 'conformita',
    area: 'compliance',
    section: t('Conformità'),
    items: [
      { to: '/nis2', label: t('NIS2 Compliance') },
      { to: '/dora', label: t('DORA Compliance') },
      { to: '/cra', label: t('CRA Compliance') },
      // The ICT providers of the registry: the DORA register and the NIS2
      // supply chain start from here.
      { to: '/suppliers/ict', label: t('Fornitori ICT'), anche: true },
    ],
  },
  {
    // Le prove: chi ha fatto cosa, e i report da consegnare.
    id: 'registri',
    area: 'compliance',
    section: t('Registri e report'),
    items: [
      { to: '/audit', label: t('Audit / Log') },
      { to: '/reports', label: t('Report'), anche: true },
    ],
  },

  // ---------------------------------------------------------- CENSO MED
  {
    // The medical devices area for clinical engineering (ADR-0027): only with
    // the CENSO MED licence (`useMed`), otherwise it does not exist.
    id: 'censo_med',
    area: 'censo_med',
    section: t('Elettromedicali'),
    items: [
      { to: '/medical', label: t('Panoramica'), end: true },
      { to: '/medical/devices', label: t('Dispositivi') },
      { to: '/medical/where', label: t('Dove sono') },
      { to: '/medical/connections', label: t('Collegamenti') },
      { to: '/medical/systems', label: t('Sistemi clinici') },
      { to: '/medical/map', label: t('Mappa clinica') },
      { to: '/medical/wire-identity', label: t('Identità dal filo') },
      { to: '/medical/usage', label: t('Utilizzo') },
      { to: '/medical/availability', label: t('Disponibilità') },
      { to: '/medical/checks', label: t('Manutenzioni e verifiche') },
      { to: '/medical/models', label: t('Catalogo modelli') },
      { to: '/medical/recalls', label: t('Richiami e avvisi') },
      { to: '/medical/import', label: t('Importa dal CMMS') },
      { to: '/medical/sources', label: t('Paese e basi dati') },
    ],
    prodotto: 'med',
  },

  // ------------------------------------------------------- CENSO ACCESS
  {
    // The register of the accesses (ADR-0030): only with the licence, for the
    // administrators and the roles that name the module (`useStatoAccess`) —
    // segregated: a role's «everything» does not open it.
    id: 'censo_access',
    area: 'censo_access',
    section: t('Registro degli accessi'),
    items: [
      { to: '/access', label: t('Panoramica'), end: true },
      { to: '/access/events', label: t('Accessi') },
      { to: '/access/people', label: t('Persone') },
      { to: '/access/posture', label: t('Postura delle utenze') },
      { to: '/access/privileges', label: t('Privilegi') },
      { to: '/access/accounts', label: t('Gestione utenze') },
      { to: '/access/detections', label: t('Rilevamenti') },
      { to: '/access/reports', label: t('Report') },
      { to: '/access/sources', label: t('Fonti') },
      { to: '/access/settings', label: t('Impostazioni') },
      { to: '/access/ledger', label: t('Registro dell’area') },
    ],
    prodotto: 'access',
  },

  // ----------------------------------------------------------- Settings
  {
    id: 'amministrazione',
    area: 'settings',
    prodotto: 'comune',
    section: t('Amministrazione'),
    items: [
      // Gli utenti prima delle impostazioni: chi cerca «dove aggiungo un
      // collega» non deve scorrere impostazioni che non c'entrano.
      { to: '/users', label: t('Utenti') },
      { to: '/settings', label: t('Impostazioni') },
      { to: '/api-keys', label: t('Chiavi API') },
      { to: '/master-data', label: t('Anagrafiche'), anche: true },
      { to: '/audit', label: t('Audit / Log'), anche: true },
      // «Quanto sto consumando» della licenza.
      { to: '/usage', label: t('Utilizzo') },
      // Qui si **decide** come la piattaforma legge gli oggetti: una regola
      // scritta male riscrive l'inventario di tutti.
      { to: '/taxonomy', label: t('Riconoscimento') },
    ],
  },
  ]
}

/**
 * A quale sezione della guida porta ogni pagina.
 *
 * La chiave è il prefisso di rotta **più specifico** che si trova; il ripiego è
 * la sezione del gruppo di navigazione. Sta qui, accanto alla navigazione,
 * perché è la stessa domanda — «dove sono» — vista dall'altra parte: il
 * collegamento «come funziona questa pagina» compare su ogni pagina senza che
 * ogni pagina debba saperlo.
 */
const GUIDA_DI: Array<[string, string]> = [
  ['/presence', 'presenza'],
  ['/capacity', 'capacita'],
  ['/eagle', 'eagle'],
  ['/medical', 'censo-med'],
  ['/access', 'censo-access'],
  ['/virtual-map', 'virtualizzazione'],
  ['/vinventory', 'virtualizzazione'],
  ['/cloud-map', 'cloud'],
  ['/cinventory', 'cloud'],
  ['/container-inventory', 'virtualizzazione'],
  ['/cost-center', 'inventario'],
  ['/software-flow', 'sbom'],
  ['/software-map', 'sbom'],
  ['/software-list', 'sbom'],
  ['/software-eol', 'sbom'],
  ['/software-licenses', 'sbom'],
  ['/software-supply', 'devsecops'],
  ['/tags', 'inventario'],
  ['/addresses', 'inventario'],
  ['/routing', 'rete'],
  ['/firewall', 'sorgenti-live'],
  ['/wazuh', 'sorgenti-live'],
  ['/cynet', 'sorgenti-live'],
  ['/dns', 'sorgenti-live'],
  ['/backup', 'sorgenti-live'],
  ['/ipam', 'sorgenti-live'],
  ['/awx', 'sorgenti-live'],
  ['/integrations', 'primi-passi'],
  ['/scan-stats', 'inventario'],
  ['/audit', 'amministrazione'],
  ['/dobermann', 'sicurezza'],
  ['/honeypot', 'sicurezza'],
  ['/device-configs', 'backup'],
  ['/inventory-agents', 'agenti'],
  ['/security-health', 'sicurezza'],
  ['/ai-usage', 'sicurezza'],
  ['/encryption', 'sicurezza'],
  ['/certificates', 'sicurezza'],
  ['/bitlocker', 'sicurezza'],
  ['/plaintext-traffic', 'sicurezza'],
  ['/nis2', 'sicurezza'],
  ['/dora', 'sicurezza'],
  ['/cra', 'sicurezza'],
  ['/services', 'servizi'],
  ['/business-apps', 'servizi'],
  ['/flows', 'traffico'],
  ['/network', 'rete'],
  ['/topology', 'rete'],
  ['/cis', 'inventario'],
  ['/assets', 'inventario'],
  ['/contracts', 'inventario'],
  ['/suppliers', 'inventario'],
  ['/users', 'amministrazione'],
  ['/api-keys', 'amministrazione'],
  ['/settings', 'amministrazione'],
  ['/master-data', 'amministrazione'],
  ['/taxonomy', 'amministrazione'],
  ['/pools', 'primi-passi'],
  ['/discovery-plans', 'primi-passi'],
  ['/jobs', 'primi-passi'],
  ['/schedules', 'primi-passi'],
  ['/fleet', 'primi-passi'],
  ['/probe-groups', 'primi-passi'],
  ['/credentials', 'primi-passi'],
  ['/connectors', 'amministrazione'],
  ['/reports', 'inventario'],
  ['/usage', 'amministrazione'],
  ['/', 'dashboard'],
]

function sezioneGuida(pathname: string): string {
  for (const [prefisso, sezione] of GUIDA_DI) {
    if (prefisso === '/' ? pathname === '/' : pathname.startsWith(prefisso)) {
      return sezione
    }
  }
  return 'primi-passi'
}

/**
 * L'icona della sezione: un segno, non un'illustrazione.
 *
 * Serve a **riconoscere** il gruppo prima di leggerne il nome — l'occhio
 * aggancia le forme molto prima delle parole, e con nove sezioni la differenza
 * si sente. Tratti semplici in `currentColor`, così seguono il tema senza una
 * variante per il buio.
 */
function IconaSezione({ id, size = 16 }: { id: string; size?: number }) {
  const tratti: Record<string, ReactNode> = {
    preferiti: (
      <path d="m12 3.6 2.6 5.3 5.8.8-4.2 4.1 1 5.8L12 16.9l-5.2 2.7 1-5.8-4.2-4.1 5.8-.8L12 3.6Z" />
    ),
    panoramica: (
      <>
        <rect x="3.5" y="3.5" width="7" height="7" rx="1" />
        <rect x="13.5" y="3.5" width="7" height="7" rx="1" />
        <rect x="3.5" y="13.5" width="7" height="7" rx="1" />
        <rect x="13.5" y="13.5" width="7" height="7" rx="1" />
      </>
    ),
    servizi: (
      <>
        <rect x="3.5" y="7.5" width="17" height="12" rx="2" />
        <path d="M9 7.5V6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v1.5M3.5 12.5h17" />
      </>
    ),
    inventario: (
      <>
        <rect x="3.5" y="4" width="17" height="4.5" rx="1" />
        <path d="M5.5 8.5v10a1.5 1.5 0 0 0 1.5 1.5h10a1.5 1.5 0 0 0 1.5-1.5v-10M10 13h4" />
      </>
    ),
    traffico: (
      <path d="M4 8h12.5m0 0-3-3m3 3-3 3M20 16H7.5m0 0 3-3m-3 3 3 3" />
    ),
    // Inventario Software: stacked packages.
    software: (
      <>
        <path d="M12 3.5 20 7.5l-8 4-8-4 8-4Z" />
        <path d="m4 12 8 4 8-4M4 16.5l8 4 8-4" />
      </>
    ),
    sicurezza: (
      <path d="M12 3.5 18.5 6v5.2c0 4.6-3.2 7.5-6.5 9.3-3.3-1.8-6.5-4.7-6.5-9.3V6L12 3.5Z" />
    ),
    // CENSO MED: a heart with a pulse.
    censo_med: (
      <>
        <path d="M12 20s-7.5-4.6-7.5-10A4.3 4.3 0 0 1 12 7.3 4.3 4.3 0 0 1 19.5 10c0 5.4-7.5 10-7.5 10Z" />
        <path d="M6 12.5h3l1.5-2.5 2 4 1.5-1.5H18" />
      </>
    ),
    // CENSO ACCESS: a key in a shield — who gets in, kept safe.
    censo_access: (
      <>
        <path d="M12 3.5 18.5 6v5.2c0 4.6-3.2 7.5-6.5 9.3-3.3-1.8-6.5-4.7-6.5-9.3V6L12 3.5Z" />
        <circle cx="12" cy="10" r="2" />
        <path d="M12 12v4m0-2h1.6" />
      </>
    ),
    // Eagle: un occhio — il monitoraggio guarda come stanno le cose.
    eagle: (
      <>
        <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
        <circle cx="12" cy="12" r="2.8" />
      </>
    ),
    'prodotto:eagle': (
      <>
        <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
        <circle cx="12" cy="12" r="2.8" />
      </>
    ),
    // CENSO: l'inventario — la pila di ciò che c'è.
    'prodotto:censo': (
      <>
        <path d="M12 3.5 20 7.5l-8 4-8-4 8-4Z" />
        <path d="m4 12 8 4 8-4M4 16.5l8 4 8-4" />
      </>
    ),
    raccolta: (
      <>
        <path d="M12 4.5v9m0 0 3.5-3.5M12 13.5 8.5 10" />
        <path d="M4.5 16.5v2a1.5 1.5 0 0 0 1.5 1.5h12a1.5 1.5 0 0 0 1.5-1.5v-2" />
      </>
    ),
    integrazioni: (
      <path d="M9 3.5V8m6-4.5V8M7 8h10v3.5a5 5 0 0 1-10 0V8Zm5 8.5v4" />
    ),
    analisi: (
      <path d="M4 20h16M6.5 20v-7m5.5 7V5.5m5.5 14.5v-10" />
    ),
    // DevSecOps: il ciclo build-rilascio.
    devsecops_software: (
      <path d="M12 12c-2-2.6-3.6-4-5.5-4a4 4 0 0 0 0 8c1.9 0 3.5-1.4 5.5-4Zm0 0c2 2.6 3.6 4 5.5 4a4 4 0 0 0 0-8c-1.9 0-3.5 1.4-5.5 4Z" />
    ),
    // Sicurezza della pipeline: un cancello.
    devsecops_governo: (
      <path d="M4 20V8.5L12 4l8 4.5V20M4 12h16M8 12v8m8-8v8" />
    ),
    // Cicli di vita e costi: una clessidra.
    ciclo_vita: (
      <path d="M7 3.5h10M7 20.5h10M8 3.5c0 4.5 8 4.5 8 8.5s-8 4-8 8.5m8-17c0 4.5-8 4.5-8 8.5s8 4 8 8.5" />
    ),
    // Difese e sorgenti: uno scudo con un segnale.
    difese: (
      <>
        <path d="M12 3.5 18.5 6v5.2c0 4.6-3.2 7.5-6.5 9.3-3.3-1.8-6.5-4.7-6.5-9.3V6L12 3.5Z" />
        <path d="M9 12h1.5l1-2.5 2 5 1-2.5H15" />
      </>
    ),
    // Beni: una scatola.
    beni: (
      <>
        <path d="M12 3.5 20 7.5v9l-8 4-8-4v-9l8-4Z" />
        <path d="m4 7.5 8 4 8-4M12 11.5v9" />
      </>
    ),
    contratti: (
      <>
        <path d="M6.5 3.5h8l4 4v13h-12v-17Z" />
        <path d="M14.5 3.5v4h4M9 12h6m-6 3h4" />
      </>
    ),
    conformita: (
      <>
        <circle cx="12" cy="10" r="6" />
        <path d="m9.5 10 1.8 1.8 3.2-3.4M8.5 15l-1.5 5.5 5-2.5 5 2.5-1.5-5.5" />
      </>
    ),
    // Registri e report: un registro con le righe.
    registri: (
      <>
        <rect x="5" y="3.5" width="14" height="17" rx="1.5" />
        <path d="M8.5 8h7m-7 4h7m-7 4h4" />
      </>
    ),
    amministrazione: (
      <>
        <path d="M4 7h9m5 0h2M4 12h3m5 0h8M4 17h11m5 0h0.5" />
        <circle cx="15" cy="7" r="2" />
        <circle cx="9" cy="12" r="2" />
        <circle cx="17" cy="17" r="2" />
      </>
    ),
  }
  return (
    <svg
      className="sidebar__icon"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {tratti[id] ?? <circle cx="12" cy="12" r="8" />}
    </svg>
  )
}

/** Confronto senza maiuscole né accenti: «credenziali» trova «Credenziali». */
function norma(testo: string): string {
  return testo
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}

const CHIAVE_SEZIONI = 'censo.nav.chiuse'
// Wide screens: whether the menu is pinned (takes its column) or hidden (opens
// over the page from the left edge), and its width. Per browser, like the
// open sections.
const KEY_PINNED = 'censo.nav.pinned'
const KEY_MODE = 'censo.nav.mode'
const KEY_WIDTH = 'censo.nav.width'
const KEY_FAVORITES = 'censo.nav.favorites'
const KEY_RECENT = 'censo.nav.recent'
const KEY_AREA = 'censo.nav.area'
const KEY_AREA_ULTIMA = 'censo.nav.area.ultima'
const NAV_WIDTH = { min: 200, default: 264, max: 440 }
const MAX_RECENT = 8

/** Wide screens: the whole menu, a column of icons, or nothing (the menu
 *  opens over the page). */
type NavMode = 'expanded' | 'rail' | 'hidden'

function readMode(): NavMode {
  try {
    const m = localStorage.getItem(KEY_MODE)
    if (m === 'expanded' || m === 'rail' || m === 'hidden') return m
    if (localStorage.getItem(KEY_PINNED) === '0') return 'hidden'
  } catch {
    // default below
  }
  return 'expanded'
}

function readList(key: string): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(key) ?? '[]')
    return Array.isArray(v) ? v.filter((x) => typeof x === 'string') : []
  } catch {
    return []
  }
}

function readWidth(): number {
  try {
    const w = Number(localStorage.getItem(KEY_WIDTH))
    return w >= NAV_WIDTH.min && w <= NAV_WIDTH.max ? w : NAV_WIDTH.default
  } catch {
    return NAV_WIDTH.default
  }
}

function store(key: string, value: string) {
  try {
    localStorage.setItem(key, value)
  } catch {
    // Remembered for this session only.
  }
}

/** Wide screen: the menu can be pinned or hidden (below 900px it is a drawer). */
function wideScreen(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(min-width: 901px)').matches
}

/** Whether a key press is someone typing (the shortcut must not fire then). */
function typing(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  if (!el) return false
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable
}

/** The three menu modes as small pictures: the whole menu, icons, nothing. */
function ModeIcon({ mode }: { mode: NavMode }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
      strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4.5" width="18" height="15" rx="2" />
      {mode === 'expanded' && <path d="M9.5 4.5v15M5 8.5h2.5M5 11.5h2.5M5 14.5h2.5" />}
      {mode === 'rail' && <path d="M7 4.5v15M5 8.5h0M5 11.5h0M5 14.5h0" strokeLinecap="round" strokeWidth="2" />}
      {mode === 'hidden' && <path d="m10 9-3 3 3 3" strokeLinecap="round" />}
    </svg>
  )
}

function leggiChiuse(): string[] {
  try {
    const grezzo = localStorage.getItem(CHIAVE_SEZIONI)
    // Mai impostato: si parte **compatti**, tutte le sezioni chiuse. È il menu
    // ordinato che chi apre la console si aspetta; la sezione della pagina
    // corrente resta comunque aperta (vedi `aperta` nel render), così si vede
    // sempre dove ci si trova, e ogni titolo apre la sua.
    if (grezzo === null) return costruisciNav().map((group) => group.id)
    const lista = JSON.parse(grezzo)
    return Array.isArray(lista) ? lista.filter((x) => typeof x === 'string') : []
  } catch {
    // Modalità privata o valore corrotto: si parte compatti.
    return costruisciNav().map((group) => group.id)
  }
}

export default function Layout() {
  const { principal, logout, switchTenant } = useAuth()
  const permessiModuli = usePermessiModuli()
  const { pathname } = useLocation()
  const naviga = useNavigate()
  const NAV = useMemo(costruisciNav, [])
  const AREE = useMemo(costruisciAree, [])
  // The portal area in use, and the last page opened in each (a click on an
  // area brings back there, not to its first page every time).
  const [areaScelta, setAreaScelta] = useState<AreaId | null>(() => {
    try {
      return localStorage.getItem(KEY_AREA) as AreaId | null
    } catch {
      return null
    }
  })
  const [ultimaPerArea, setUltimaPerArea] = useState<Record<string, string>>(() => {
    try {
      return JSON.parse(localStorage.getItem(KEY_AREA_ULTIMA) ?? '{}') as Record<string, string>
    } catch {
      return {}
    }
  })
  const ALL_ITEMS = useMemo(() => NAV.flatMap((group) => group.items), [NAV])
  // Su schermo stretto la navigazione è un cassetto. Sopra i 900px apre il menu
  // sopra la pagina quando non è fissato (vedi `pinned`).
  const [menuOpen, setMenuOpen] = useState(false)
  // Wide screens: pinned = the menu keeps its column (as always); unpinned =
  // the page gets the whole width and the menu opens over it — from the left
  // edge, the ☰ button or the «[» key — and closes when the pointer leaves.
  const [mode, setModeState] = useState<NavMode>(readMode)
  const pinned = mode === 'expanded'
  const [navWidth, setNavWidth] = useState(readWidth)
  // Favourite pages (a star on each item) and the last pages visited: first in
  // the menu, in the icons and in the ⌘K search.
  const [favorites, setFavorites] = useState<string[]>(() => readList(KEY_FAVORITES))
  const [recent, setRecent] = useState<string[]>(() => readList(KEY_RECENT))
  const navRef = useRef<HTMLElement | null>(null)
  const menuButtonRef = useRef<HTMLButtonElement | null>(null)
  const hoverTimer = useRef<number | undefined>(undefined)

  function setMode(value: NavMode) {
    setModeState(value)
    setMenuOpen(false)
    store(KEY_MODE, value)
  }

  function toggleFavorite(to: string) {
    setFavorites((prev) => {
      const next = prev.includes(to) ? prev.filter((x) => x !== to) : [...prev, to]
      store(KEY_FAVORITES, JSON.stringify(next))
      return next
    })
  }

  // ☰ and «[»: the whole menu folds into icons and back; with the menu hidden
  // they open it over the page.
  function toggleMenu() {
    if (!wideScreen()) {
      setMenuOpen((open) => !open)
    } else if (mode === 'expanded') {
      setMode('rail')
    } else if (mode === 'rail' && !menuOpen) {
      setMode('expanded')
    } else {
      setMenuOpen((open) => !open)
    }
  }

  // «[» shows or hides the menu from anywhere but a text field; Esc closes it
  // when it is open over the page.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && menuOpen) {
        setMenuOpen(false)
        return
      }
      if (e.key === '[' && !e.ctrlKey && !e.metaKey && !e.altKey && !typing(e.target)) {
        e.preventDefault()
        toggleMenu()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  // Open over the page: a click outside closes it.
  useEffect(() => {
    if (!menuOpen || pinned || !wideScreen()) return
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (navRef.current?.contains(target) || menuButtonRef.current?.contains(target)) return
      setMenuOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [menuOpen, pinned])

  function hoverEdge(enter: boolean) {
    window.clearTimeout(hoverTimer.current)
    if (enter) hoverTimer.current = window.setTimeout(() => setMenuOpen(true), 140)
  }

  function leaveMenu() {
    if (pinned || !wideScreen()) return
    window.clearTimeout(hoverTimer.current)
    hoverTimer.current = window.setTimeout(() => {
      // Stays while the filter is in use: leaving the menu to read is not
      // closing it.
      if (navRef.current?.contains(document.activeElement) && filtroRef.current?.value) return
      setMenuOpen(false)
    }, 380)
  }

  // Dragging the right edge of the pinned menu sets its width; a double click
  // gives the default back.
  function startResize(e: React.PointerEvent<HTMLDivElement>) {
    e.preventDefault()
    const startX = e.clientX
    const startW = navWidth
    let last = startW
    const move = (ev: PointerEvent) => {
      last = Math.max(NAV_WIDTH.min, Math.min(NAV_WIDTH.max, startW + ev.clientX - startX))
      setNavWidth(last)
    }
    const up = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      document.body.classList.remove('nav-resizing')
      store(KEY_WIDTH, String(last))
    }
    document.body.classList.add('nav-resizing')
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }

  // Il filtro del menu: con ventiquattro voci, digitare tre lettere è più
  // veloce che scorrere nove sezioni — ed è l'unico modo rapido sul telefono.
  const [filtro, setFiltro] = useState('')
  const filtroRef = useRef<HTMLInputElement | null>(null)
  // Just reloaded by a tenant switch on the same section (sezioneTenant.ts):
  // once the menu of the new tenant is known, the section stays only if the
  // menu has it.
  const [dopoCambio, setDopoCambio] = useState(() => {
    try {
      return sessionStorage.getItem(DOPO_CAMBIO_TENANT) === '1'
    } catch {
      return false
    }
  })

  // Le sezioni chiuse, ricordate fra le sessioni. Di default il menu parte
  // **compatto** (tutte chiuse tranne quella della pagina corrente); da lì
  // l'utente apre ciò che vuole e la scelta resta. Si memorizza chi è chiuso,
  // così quando compare una sezione nuova su un menu già personalizzato la si
  // vede — non nasce nascosta in fondo a una lista già chiusa.
  const [chiuse, setChiuse] = useState<string[]>(leggiChiuse)

  // Si chiude cambiando pagina, e il filtro si azzera. Senza, dopo un tocco il
  // cassetto resterebbe aperto davanti alla pagina appena scelta, e al giro
  // dopo il menu sarebbe ancora ridotto alle voci dell'ultima ricerca.
  useEffect(() => {
    setMenuOpen(false)
    setFiltro('')
  }, [pathname])

  function commuta(id: string) {
    setChiuse((prima) => {
      const dopo = prima.includes(id) ? prima.filter((x) => x !== id) : [...prima, id]
      try {
        localStorage.setItem(CHIAVE_SEZIONI, JSON.stringify(dopo))
      } catch {
        // La scelta vale per questa sessione e basta.
      }
      return dopo
    })
  }

  // Il percorso si ricava dalla rotta: una tabella a parte andrebbe fuori
  // sincrono alla prima pagina aggiunta.
  //
  // Vince la corrispondenza più **lunga**, non la prima trovata: con `/flows` e
  // `/flows/entry-points` nello stesso elenco, `startsWith` fa corrispondere
  // anche il primo, e sarebbe l'ordine di dichiarazione a decidere quale voce si
  // illumina.
  const current =
    ALL_ITEMS.filter((item) =>
      item.end ? pathname === item.to : pathname.startsWith(item.to),
    ).sort((a, b) => b.to.length - a.to.length)[0] ?? ALL_ITEMS[0]

  const gruppoCorrenteNav = NAV.find((group) => group.items.includes(current))

  // The pages visited, most recent first (menu pages only, the current one too).
  const matchesCurrent = current && (current.end ? pathname === current.to : pathname.startsWith(current.to))
  useEffect(() => {
    if (!matchesCurrent || !current) return
    setRecent((prev) => {
      if (prev[0] === current.to) return prev
      const next = [current.to, ...prev.filter((x) => x !== current.to)].slice(0, MAX_RECENT)
      store(KEY_RECENT, JSON.stringify(next))
      return next
    })
  }, [current?.to, matchesCurrent])

  // Con il filtro attivo restano solo le voci che corrispondono — per etichetta
  // o per nome della sezione — e le sezioni vuote spariscono del tutto.
  const q = norma(filtro.trim())
  // Il ruolo personalizzato: le voci nascoste non ci sono, quelle disattivate
  // si vedono in grigio (vedi auth/permessi.ts). Senza ruolo personalizzato
  // `livelloRotta` dà sempre null e il menu è quello di sempre.
  const permessi = principal?.permissions
  // CENSO, Eagle o entrambi (ADR-0022): decide com'è fatto il menu.
  const statoEagle = useStatoEagle()
  const eagle = statoEagle.attivo
  const modo: 'censo' | 'eagle' | 'entrambi' = !eagle ? 'censo' : statoEagle.standalone ? 'eagle' : 'entrambi'
  const prodottoDi = (g: GruppoNav) => g.prodotto ?? 'censo'
  // CENSO MED: licensed and open to the role (a 403 on its status reads as off).
  const statoMed = useStatoMed()
  const med = statoMed.attivo
  // CENSO ACCESS: licensed, for administrators and the roles naming it (a 403 is «off»).
  const statoAccess = useStatoAccess()
  const accessi = statoAccess.attivo
  const livelloVoce = (to: string) => livelloRotta(permessi, to)
  // Every section the tenant (product, Eagle) and the role let the user see,
  // with its visible items: the areas, the icons and the search start here.
  const ammessi = NAV.filter((group) =>
    // Eagle esiste solo con l'add-on; con Eagle da solo le sezioni di CENSO no.
    prodottoDi(group) === 'eagle' ? eagle : prodottoDi(group) === 'med' ? med : prodottoDi(group) === 'access' ? accessi
      : prodottoDi(group) === 'censo' ? modo !== 'eagle' : true,
  ).map((group) => {
    // Con Eagle da solo, l'elenco dei CI sta dentro Eagle: è da lì che si
    // attiva il monitoraggio di un oggetto.
    const voci =
      modo === 'eagle' && group.id === 'eagle'
        ? [...group.items, { to: '/cis', label: t('Configuration Item') }]
        : group.items
    return { ...group, ammesse: voci.filter((item) => livelloVoce(item.to) !== 'hidden') }
  }).filter((group) => group.ammesse.length > 0)

  // The areas the user can open: those with at least one page the role opens.
  const areeVisibili: AreaVisibile[] = AREE.flatMap((a) => {
    const voci = ammessi.filter((g) => g.area === a.id).flatMap((g) => g.ammesse)
      .filter((item) => livelloVoce(item.to) !== 'disabled')
    if (voci.length === 0) return []
    const ultima = ultimaPerArea[a.id]
    const prima = ultima && voci.some((v) => ultima === v.to || ultima.startsWith(`${v.to}/`)) ? ultima : voci[0].to
    return [{ ...a, prima, pagine: new Set(voci.map((v) => v.to)).size }]
  })
  // The areas of the page shown: a link (a favourite, ⌘K, a page of another
  // area) opens it in the area in use when it is there, else in its first.
  // Its first area (where it is not a second door) comes first.
  const areeDellaPagina = ammessi.flatMap((g) => g.ammesse.filter((i) => g.area !== 'home' && i.to === current.to)
    .map((i) => ({ area: g.area as AreaId, anche: Boolean(i.anche) })))
    .sort((a, b) => Number(a.anche) - Number(b.anche)).map((x) => x.area)
  const visibile = (id: AreaId | null) => Boolean(id && areeVisibili.some((a) => a.id === id))
  const areaInUso: AreaId | null =
    areeDellaPagina.length && !areeDellaPagina.includes(areaScelta as AreaId)
      ? areeDellaPagina[0]
      : visibile(areaScelta) ? areaScelta : areeVisibili[0]?.id ?? null
  useEffect(() => {
    if (!areaInUso || areaInUso === areaScelta) return
    setAreaScelta(areaInUso)
    store(KEY_AREA, areaInUso)
  }, [areaInUso, areaScelta])
  useEffect(() => {
    if (!areaInUso || !areeDellaPagina.includes(areaInUso) || ultimaPerArea[areaInUso] === pathname) return
    const dopo = { ...ultimaPerArea, [areaInUso]: pathname }
    setUltimaPerArea(dopo)
    store(KEY_AREA_ULTIMA, JSON.stringify(dopo))
  }, [areaInUso, pathname, areeDellaPagina.join(','), ultimaPerArea])

  function vaiArea(id: AreaId) {
    const a = areeVisibili.find((x) => x.id === id)
    if (!a) return
    setAreaScelta(id)
    store(KEY_AREA, id)
    naviga(a.prima)
  }

  // The menu shows the sections of the area in use; the filter looks in all
  // of them (a match in another area is still a match).
  const gruppi = ammessi.filter((group) => group.area !== 'home' && (q !== '' || group.area === areaInUso))
    .map((group) => ({
      ...group,
      visibili: q
        ? norma(group.section).includes(q)
          ? group.ammesse
          : group.ammesse.filter((item) => norma(item.label).includes(q))
        : group.ammesse,
    })).filter((group) => group.visibili.length > 0)
  const home = ammessi.find((g) => g.area === 'home')?.ammesse[0]
  const gruppoCorrente =
    gruppi.find((g) => g.area === areaInUso && g.visibili.some((i) => i.to === current.to))
    ?? gruppi.find((g) => g.visibili.some((i) => i.to === current.to)) ?? gruppoCorrenteNav
  const areaCorrente = areeVisibili.find((a) => a.id === areaInUso)
  // Una sezione del menu, con le sue voci. `annidata`: dentro una delle due
  // voci principali (CENSO, Eagle), quando il tenant le ha entrambe.
  const disegnaGruppo = (group: (typeof gruppi)[number], annidata = false) => {
            // La sezione della pagina corrente resta aperta anche se era stata
            // chiusa: nascondere «dove sono» è l'unico caso in cui la memoria
            // farebbe danno. Con il filtro attivo sono tutte aperte, perché una
            // corrispondenza dentro una sezione chiusa è una corrispondenza
            // che non si vede.
            const aperta =
              q !== '' || group.id === gruppoCorrente?.id || !chiuse.includes(group.id)
            return (
              <div className={`sidebar__group${annidata ? ' sidebar__group--annidato' : ''}`} key={group.id}>
                <button
                  type="button"
                  className="sidebar__section"
                  aria-expanded={aperta}
                  onClick={() => {
                    if (q === '') commuta(group.id)
                  }}
                >
                  <IconaSezione id={group.id} />
                  {group.section}
                  <span className="sidebar__chevron" aria-hidden="true" />
                </button>
                {aperta &&
                  group.visibili.map((item) =>
                    livelloVoce(item.to) === 'disabled' ? (
                      <span
                        key={item.to}
                        className="navlink navlink--disattivata"
                        aria-disabled="true"
                        title={t('Disattivato per il tuo ruolo')}
                      >
                        {item.label}
                        <span className="navlink__lucchetto" aria-hidden="true">
                          🔒
                        </span>
                      </span>
                    ) : (
                    <div className="navitem" key={item.to}>
                      <NavLink
                        to={item.to}
                        end={item.end}
                        className={({ isActive }) =>
                          `navlink${isActive ? ' navlink--active' : ''}`
                        }
                      >
                        {item.label}
                      </NavLink>
                      <StarButton on={favorites.includes(item.to)} onToggle={() => toggleFavorite(item.to)} />
                    </div>
                    ),
                  )}
              </div>
            )
            }

  // Every page the user can open, by path: for the favourites, the recents and
  // the ⌘K search.
  const pages = ammessi.flatMap((g) => g.ammesse
    .filter((item) => livelloVoce(item.to) !== 'disabled')
    .map((item) => ({ to: item.to, label: item.label, end: item.end,
      section: g.area === 'home' ? g.section : `${AREE.find((a) => a.id === g.area)?.label ?? ''} · ${g.section}` })))
    .filter((p, i, tutte) => tutte.findIndex((x) => x.to === p.to) === i)
  const pageOf = new Map(pages.map((p) => [p.to, p]))
  const favoritePages = favorites.map((to) => pageOf.get(to)).filter((p): p is (typeof pages)[number] => Boolean(p))
  const recentPages = recent.map((to) => pageOf.get(to)).filter((p): p is (typeof pages)[number] => Boolean(p))
  // Unfiltered groups for the icons (the filter is the expanded menu's).
  const railGroups = ammessi.filter((group) => group.area === 'home' || group.area === areaInUso)
    .map((group) => ({ id: group.id, section: group.section, items: group.ammesse }))
  const railWithFavorites = favoritePages.length
    ? [{ id: 'preferiti', section: t('Preferiti'), items: favoritePages }, ...railGroups]
    : railGroups

  // The favourites, first in the expanded menu (not while filtering: then
  // the matches are what counts).
  const disegnaPreferiti = () => {
    if (q !== '' || favoritePages.length === 0) return null
    const aperta = !chiuse.includes('preferiti')
    return (
      <div className="sidebar__group sidebar__group--preferiti" key="preferiti">
        <button type="button" className="sidebar__section" aria-expanded={aperta} onClick={() => commuta('preferiti')}>
          <IconaSezione id="preferiti" />
          {t('Preferiti')}
          <span className="sidebar__chevron" aria-hidden="true" />
        </button>
        {aperta && favoritePages.map((item) => (
          <div className="navitem" key={item.to}>
            <NavLink to={item.to} end={item.end}
              className={({ isActive }) => `navlink${isActive ? ' navlink--active' : ''}`}>
              {item.label}
            </NavLink>
            <StarButton on onToggle={() => toggleFavorite(item.to)} />
          </div>
        ))}
      </div>
    )
  }

  const livelloPagina = livelloRotta(permessi, pathname)
  const paginaChiusa = livelloPagina === 'hidden' || livelloPagina === 'disabled'

  // After a tenant switch: the section stays if the new tenant's menu shows it
  // and it is open; otherwise home, as before. Pages that are not menu items
  // (the guide…) always stay.
  const menuPronto = Boolean(principal) && Boolean(statoEagle.pronto) && Boolean(statoMed.pronto) && Boolean(statoAccess.pronto)
  const copre = (to: string) => (to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`))
  const sezioneAssente =
    dopoCambio && menuPronto && pathname !== '/' && (
      paginaChiusa
      || (NAV.some((g) => g.items.some((i) => copre(i.to)))
        && !ammessi.some((g) => g.ammesse.some((i) => copre(i.to) && livelloVoce(i.to) !== 'disabled')))
    )
  useEffect(() => {
    if (!dopoCambio || !menuPronto) return
    try {
      sessionStorage.removeItem(DOPO_CAMBIO_TENANT)
    } catch {
      // nothing to clear
    }
    setDopoCambio(false)
  }, [dopoCambio, menuPronto])
  if (sezioneAssente) return <Navigate to="/" replace />

  // Con Eagle da solo la pagina iniziale è quella di Eagle: la dashboard di
  // CENSO racconta un inventario che quel cliente non ha comprato.
  if (modo === 'eagle' && pathname === '/') return <Navigate to="/eagle" replace />
  // A role that sees CENSO MED and not the CMDB (clinical engineering): its
  // home is the area's, the CENSO dashboard would only show empty numbers.
  if (med && pathname === '/' && livelloVoce('/cis') === 'hidden' && livelloVoce('/medical') !== 'hidden') {
    return <Navigate to="/medical" replace />
  }
  // The same for the roles of CENSO ACCESS (Responsabile, Revisore): their home is the register.
  if (accessi && pathname === '/' && livelloVoce('/cis') === 'hidden' && livelloVoce('/access') !== 'hidden') {
    return <Navigate to="/access" replace />
  }
  // Il sinottico: la dashboard sola, a tutto schermo, senza menu né barra.
  if (pathname.startsWith('/sinottico') || pathname.startsWith('/eagle/sinottico')) return <Outlet />

  return (
    <>
      <header className="topbar">
        {/* Visibile solo sotto i 900px, dove il menu è un cassetto. `aria-expanded`
            non è decorazione: senza, chi naviga con uno screen reader non sa se
            sta per aprire o chiudere. */}
        <button
          type="button"
          ref={menuButtonRef}
          className={`topbar__menu${pinned ? ' topbar__menu--pinned' : ''}`}
          aria-label={t('Menu di navigazione')}
          aria-expanded={menuOpen}
          title={t('Mostra o nascondi il menu (tasto [)')}
          onClick={toggleMenu}
        >
          {menuOpen ? '✕' : '☰'}
        </button>
        {/* Il portale: le aree della console, in alto a sinistra. */}
        {principal && <SelettoreAree aree={areeVisibili} corrente={areaInUso} onScegli={vaiArea} />}
        <Link to="/" className="topbar__brand">
          <Marchio size={22} />
          CENSO
        </Link>

        <span className="topbar__spacer" />

        {/* La lente cerca ovunque — CI, IP, MAC, porte, software, seriali — e
            porta dove trovare. Sta nella barra perché la domanda «dov'è quella
            cosa» arriva da qualunque pagina. */}
        {principal && <GlobalSearch pages={pages} favorites={favoritePages} recent={recentPages} />}

        {/* L'antifurto del Dobermann: compare solo quando la guardia è in
            servizio, e lampeggia. Sta qui, nella barra, perché è uno stato che
            deve vedersi da qualunque pagina. */}
        {/* I distintivi di moduli che il ruolo nasconde o disattiva non si mostrano. */}
        {permessiModuli.vede('dobermann') && <DobermannBadge />}
        {permessiModuli.vede('honeypot') && <HoneypotBadge />}

        {/* Su quale cliente si sta lavorando: con più tenant a disposizione,
            non saperlo a colpo d'occhio significa poter agire su quello
            sbagliato. */}
        {principal && (principal.tenants?.length ?? 0) > 1 ? (
          <select
            className="topbar__select"
            value={principal.tenant_id}
            onChange={(e) => switchTenant(e.target.value)}
            aria-label={t('Tenant su cui operare')}
            title={t('Tenant su cui operare')}
          >
            {principal.tenants.map((t) => (
              <option key={t.tenant_id} value={t.tenant_id}>
                {/* Nome **e** etichetta: due clienti possono chiamarsi allo
                    stesso modo, e un selettore in cui ogni voce si legge
                    uguale non serve a nulla. L'etichetta è univoca. */}
                {t.name} ({t.slug})
              </option>
            ))}
          </select>
        ) : (
          principal?.tenant_name && (
            <span className="topbar__item topbar__tenant">{principal.tenant_name}</span>
          )
        )}

        <Link to="/guide" className="topbar__item topbar__link">
          {t('Guida')}
        </Link>
        <LinguaSwitch />
        <ThemeSwitch />
        {principal && <NotificheBell />}
        {principal && (
          <span className="topbar__item" title={principal.subject}>
            {principal.email ?? principal.display_name ?? principal.subject}
          </span>
        )}
        <button type="button" className="topbar__button" onClick={logout}>
          {t('Esci')}
        </button>
      </header>

      <div
        className={`layout${mode === 'hidden' ? ' layout--libero' : mode === 'rail' ? ' layout--rail' : ''}`}
        style={{ '--nav-width': `${navWidth}px` } as CSSProperties}
      >
        {/* Hidden: an edge on the left that opens the menu when the pointer
            rests on it. */}
        {mode === 'hidden' && !menuOpen && (
          <div className="menu-bordo" aria-hidden="true"
            onMouseEnter={() => hoverEdge(true)} onMouseLeave={() => hoverEdge(false)} />
        )}
        {mode === 'rail' && (
          <NavRail
            groups={railWithFavorites}
            currentGroupId={gruppoCorrente?.id}
            icon={(id) => <IconaSezione id={id} size={20} />}
            isDisabled={(to) => livelloVoce(to) === 'disabled'}
            isFavorite={(to) => favorites.includes(to)}
            toggleFavorite={toggleFavorite}
            onExpand={() => setMode('expanded')}
          />
        )}
        {/* Lo sfondo scuro che chiude il cassetto toccando fuori: su un telefono
            è il gesto che tutti provano per primo, e senza di esso l'unica uscita
            sarebbe centrare di nuovo il pulsante in alto a sinistra. */}
        {menuOpen && (
          <div className="scrim" onClick={() => setMenuOpen(false)} aria-hidden="true" />
        )}

        <nav
          ref={navRef}
          className={`sidebar${menuOpen ? ' sidebar--open' : ''}`}
          aria-label={t('Navigazione principale')}
          onMouseEnter={() => window.clearTimeout(hoverTimer.current)}
          onMouseLeave={leaveMenu}
        >
          <div className="sidebar__brand">
            <div>
              {modo === 'eagle' ? 'CENSO Eagle' : 'CENSO'}
              {/* L'etichetta del tenant, non l'identificativo: è il modo in cui
                  il cliente si chiama, e si legge senza doverlo decifrare. */}
              <small>{principal?.tenant_slug ?? 'Console'}</small>
            </div>
            <div className="sidebar__modes" role="group" aria-label={t('Come mostrare il menu')}>
              {(['expanded', 'rail', 'hidden'] as NavMode[]).map((m) => (
                <button
                  key={m}
                  type="button"
                  className={`sidebar__mode${mode === m ? ' sidebar__mode--on' : ''}`}
                  aria-pressed={mode === m}
                  aria-label={m === 'expanded' ? t('Menu esteso') : m === 'rail' ? t('Menu a icone') : t('Menu nascosto')}
                  title={m === 'expanded'
                    ? t('Menu esteso, fisso a sinistra (tasto [ per passare alle icone)')
                    : m === 'rail'
                      ? t('Menu a icone: le voci di una sezione si aprono passandoci sopra')
                      : t('Menu nascosto: la pagina usa tutta la larghezza, il menu si apre dal bordo sinistro o con ☰')}
                  onClick={() => setMode(m)}
                >
                  <ModeIcon mode={m} />
                </button>
              ))}
            </div>
          </div>

          <div className="sidebar__filter">
            <input
              ref={filtroRef}
              type="search"
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setFiltro('')
              }}
              placeholder={t('Filtra il menu')}
              aria-label={t('Filtra il menu')}
            />
          </div>

          {gruppi.length === 0 && (
            <p className="sidebar__empty">{t('Nessuna voce del menu corrisponde.')}</p>
          )}

          {home && q === '' && (
            <div className="navitem sidebar__home">
              <NavLink to={home.to} end className={({ isActive }) => `navlink${isActive ? ' navlink--active' : ''}`}>
                <IconaArea id="home" size={16} /> {home.label}
              </NavLink>
            </div>
          )}
          {disegnaPreferiti()}
          {areaCorrente && q === '' && (
            <div className="sidebar__area" title={areaCorrente.description}>
              <IconaArea id={areaCorrente.id} size={18} />
              <span>{areaCorrente.label}</span>
            </div>
          )}
          {gruppi.map((g) => disegnaGruppo(g))}

          <div className="sidebar__footer">
            {principal?.custom_role && (
              <span
                className="chip chip--ruolo"
                style={{ borderColor: principal.custom_role.color, color: principal.custom_role.color }}
                title={t('Ruolo personalizzato')}
              >
                {principal.custom_role.preset ? t(principal.custom_role.name) : principal.custom_role.name}
              </span>
            )}
            {principal?.roles.map((role) => (
              <span className="chip" key={role}>
                {role}
              </span>
            ))}
            {/* Quale build si sta guardando. Senza, una console rimasta in cache
                è indistinguibile da un difetto: si cerca il guasto in un codice
                che è già corretto e semplicemente non è quello in esecuzione. */}
            <div className="sidebar__build" title={t('Build della console: {sha}', { sha: BUILD.sha })}>
              build {buildLabel()}
            </div>
            {/* Il produttore, dove stanno le informazioni di targa: chi deve
                sapere «di chi è questo software» guarda in fondo, non in cima. */}
            <div className="sidebar__produttore">CENSO · DiscoveryGuard</div>
          </div>
          {pinned && (
            <div
              className="sidebar__maniglia"
              role="separator"
              aria-orientation="vertical"
              aria-label={t('Larghezza del menu')}
              title={t('Trascina per allargare o stringere il menu; doppio clic per la larghezza normale')}
              onPointerDown={startResize}
              onDoubleClick={() => { setNavWidth(NAV_WIDTH.default); store(KEY_WIDTH, String(NAV_WIDTH.default)) }}
            />
          )}
        </nav>

        <main className="main">
          <StaleBuild />
          <BannerEsempio />

          <nav className="breadcrumbs" aria-label={t('Percorso')}>
            <Link to="/">CENSO</Link>
            {areaCorrente && pathname !== '/' && areeDellaPagina.includes(areaCorrente.id) && (
              <>
                <span className="breadcrumbs__sep" aria-hidden="true">/</span>
                <button type="button" className="breadcrumbs__area" onClick={() => vaiArea(areaCorrente.id)}>{areaCorrente.label}</button>
              </>
            )}
            {/* La sezione nel percorso, quando ne ha una con più voci: «CENSO /
                Raccolta / Sonde» dice anche **dove** si è nel menu, non solo su
                quale pagina. Con una voce sola sarebbe una ripetizione. */}
            {gruppoCorrente && gruppoCorrente.items.length > 1 && (
              <>
                <span className="breadcrumbs__sep" aria-hidden="true">
                  /
                </span>
                <span>{gruppoCorrente.section}</span>
              </>
            )}
            <span className="breadcrumbs__sep" aria-hidden="true">
              /
            </span>
            <span className="breadcrumbs__current">
              {pathname.startsWith('/guide') ? t('Guida') : current.label}
            </span>
            {/* Su ogni pagina, il ponte verso la sezione giusta della guida:
                una spiegazione che sta a un clic da dove serve, invece che in
                un documento che nessuno apre. Sulla guida stessa sparisce. */}
            {!pathname.startsWith('/guide') && (
              <Link className="breadcrumbs__help" to={`/guide#${sezioneGuida(pathname)}`}>
                {t('Come funziona questa pagina')}
              </Link>
            )}
          </nav>

          {paginaChiusa ? (
            <ModuloNonDisponibile disattivato={livelloPagina === 'disabled'} verso={primaRottaAperta(permessi)} />
          ) : (
            <>
              {livelloPagina === 'read' && (
                <p className="banner banner--info banner--sola-lettura">
                  {t('Sola lettura: con il tuo ruolo puoi consultare questo modulo ma non modificarlo.')}
                </p>
              )}
              <PortaleContext.Provider value={{ aree: areeVisibili, corrente: areaInUso, vai: vaiArea }}>
                <Outlet />
              </PortaleContext.Provider>
            </>
          )}
        </main>
      </div>
      {/* La faccina di CENSO, in basso a destra su ogni pagina: fa una
          domanda e, per ora, risponde cercando nell'inventario. */}
      {principal && <Assistente />}
    </>
  )
}

/**
 * «Stai guardando l'ambiente di esempio»: su ogni pagina finché i dati di
 * esempio ci sono, con il rimando a Impostazioni per passare all'ambiente
 * vuoto. Senza, chi prova CENSO prende per suoi i server di una sede che
 * non esiste — ed è già successo a chi guardava uno screenshot.
 */
function BannerEsempio() {
  const { principal } = useAuth()
  const isAdmin = Boolean(principal?.roles.includes('tenant_admin') || principal?.roles.includes('provider_admin'))
  const stato = useAsync(() => api.demoData(), [principal?.tenant_id])
  const [busy, setBusy] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  // «Non me lo chiedere più» per l'invito a caricare l'esempio, in questo
  // browser: chi ha deciso di partire vuoto non deve vederlo a ogni pagina.
  const [nascosto, setNascosto] = useState(() => {
    try {
      return localStorage.getItem('censo.esempio.nascosto') === '1'
    } catch {
      return false
    }
  })

  async function svuota() {
    if (!window.confirm(t('Togliere tutti i dati di esempio? I dati veri, se ci sono, restano.'))) return
    setBusy(true)
    setErrore(null)
    try {
      await api.clearDemoData()
      window.location.reload()
    } catch (e) {
      setErrore(e instanceof Error ? e.message : String(e))
      setBusy(false)
    }
  }

  async function carica() {
    setBusy(true)
    setErrore(null)
    try {
      await api.loadDemoData()
      window.location.reload()
    } catch (e) {
      setErrore(e instanceof Error ? e.message : String(e))
      setBusy(false)
    }
  }

  function nascondi() {
    setNascosto(true)
    try {
      localStorage.setItem('censo.esempio.nascosto', '1')
    } catch {
      /* vale per questa pagina */
    }
  }

  const d = stato.data
  if (!d) return null

  // L'ambiente di esempio c'è: si toglie da qui, senza passare da Impostazioni.
  if (d.loaded) {
    return (
      <p className="banner banner--info banner--esempio">
        {t('Stai guardando l’ambiente di esempio di CENSO: una sede, i suoi server e un account cloud inventati per capire la piattaforma.')}{' '}
        {isAdmin ? (
          <button type="button" className="secondary" onClick={() => void svuota()} disabled={busy}>
            {busy ? t('Un attimo…') : t('Passa all’ambiente vuoto')}
          </button>
        ) : (
          <span className="muted">{t('Chi amministra il tenant può passare all’ambiente vuoto.')}</span>
        )}
        {errore && <span className="muted"> · {errore}</span>}
      </p>
    )
  }

  // Tenant in prova e vuoto: l'invito a caricare l'esempio, da qui.
  if (d.trial && d.can_load && isAdmin && !nascosto) {
    return (
      <p className="banner banner--info banner--esempio">
        {t('L’ambiente è vuoto. Vuoi vedere la piattaforma popolata? Carica l’ambiente di esempio: una sede con rete, server, VM, un account cloud, rischi e contratti. Si toglie con un clic quando colleghi le tue sonde.')}{' '}
        <button type="button" className="secondary" onClick={() => void carica()} disabled={busy}>
          {busy ? t('Un attimo…') : t('Carica l’ambiente di esempio')}
        </button>{' '}
        <button type="button" className="secondary" onClick={nascondi} disabled={busy}>
          {t('No, parto vuoto')}
        </button>
        {errore && <span className="muted"> · {errore}</span>}
      </p>
    )
  }
  return null
}

/**
 * Avvisa quando il server serve una build diversa da quella in esecuzione.
 *
 * È il seguito necessario del timbro nel footer: sapere quale build si sta
 * guardando aiuta solo chi pensa a controllarla. Chi ha appena rilasciato una
 * modifica e non la vede non ha motivo di sospettare la cache — e passa il tempo
 * a cercare un difetto in un codice già corretto.
 *
 * Il controllo si rifà quando la finestra torna in primo piano: è il momento in
 * cui una scheda lasciata aperta per ore ha più probabilità di essere vecchia, ed
 * evita di interrogare il server a intervalli per un evento raro.
 */
function StaleBuild() {
  const [stale, setStale] = useState(false)

  useEffect(() => {
    let alive = true
    const check = () => {
      // Una volta accertato, non si torna indietro: la build in esecuzione non
      // può ringiovanire, e un avviso che compare e sparisce si scambia per un
      // difetto dell'avviso.
      if (stale) return
      void isStale().then((result) => {
        if (alive && result) setStale(true)
      })
    }

    check()
    window.addEventListener('focus', check)
    return () => {
      alive = false
      window.removeEventListener('focus', check)
    }
  }, [stale])

  if (!stale) return null

  return (
    <div className="banner banner--warn" style={{ marginBottom: 16 }}>
      <strong>{t('Questa console non è aggiornata.')}</strong>{' '}
      {t('Il server ne serve una versione più recente, ma il browser sta ancora eseguendo la build {sha}. Quello che vedi può non riflettere lo stato attuale del sistema.', { sha: BUILD.sha })}{' '}
      <button
        type="button"
        className="linklike"
        onClick={() => window.location.reload()}
      >
        {t('Ricarica')}
      </button>
    </div>
  )
}

/**
 * Al posto di una pagina che il ruolo non apre: detto, invece di una pagina
 * vuota piena di errori 403, e con la strada per tornare a qualcosa di aperto.
 */
function ModuloNonDisponibile({ disattivato, verso }: { disattivato: boolean; verso: string | null }) {
  return (
    <div className="modulo-chiuso">
      <div className="modulo-chiuso__icona" aria-hidden="true">
        🔒
      </div>
      <h2>{disattivato ? t('Modulo disattivato per il tuo ruolo') : t('Modulo non disponibile')}</h2>
      <p>
        {t('Il ruolo che ti è stato assegnato non dà accesso a questa sezione. Se ti serve, chiedi a chi amministra gli utenti del tenant.')}
      </p>
      {verso && (
        <Link className="modulo-chiuso__azione" to={verso}>
          {t('Vai a una sezione disponibile')}
        </Link>
      )}
    </div>
  )
}
